import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, Grid, Alert, Typography, Snackbar } from '@mui/material';
import BrandCard from './BrandCard';
import BrandEditModal from './BrandEditModal';
import { selectBrand, updateBrand } from '../../store/slices/productsSlice';
import { useLocalData } from '../../hooks/useLocalData';
import globalContextManager from '../../utils/globalContextManager';

/**
 * Verifica se usuário é admin.
 * @param {Object} user - Objeto do usuário
 * @returns {boolean} True se admin
 */
const isAdmin = (user) => {
  return user?.roles?.some(role => role.alias === 'admin') || false;
};

/**
 * Componente container para exibição de marcas.
 * Carrega e exibe marcas da API com layout responsivo otimizado.
 * Mobile: 2 cards por linha, Desktop: 4 cards por linha.
 * Implementa scroll automático para centralizar a última marca visualizada.
 * Admin: vê todas as marcas + botão editar.
 * Não-admin: vê apenas marcas ativas, sem botão editar.
 *
 * @component
 * @returns {JSX.Element} Container responsivo com grid de marcas e navegação contextual
 */
export default function Brand() {
  const dispatch = useDispatch();
  const { lastViewedBrand } = useSelector(state => state.products);
  const { user } = useSelector(state => state.auth);
  const { brands } = useLocalData(); // Usar dados locais
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBrandEdit, setSelectedBrandEdit] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const brandRefs = useRef({});

  const isUserAdmin = useMemo(() => isAdmin(user), [user]);

  // Dados são carregados via contexto global - não precisa de useEffect para fetch

  // Scroll para a última marca visualizada
  useEffect(() => {
    if (lastViewedBrand && brands.length > 0) {
      const brandElement = brandRefs.current[lastViewedBrand.id];
      if (brandElement) {
        setTimeout(() => {
          brandElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }, 100);
      }
    }
  }, [lastViewedBrand, brands]);

  /**
   * Marcas filtradas baseado na role do usuário.
   * Admin: todas as marcas.
   * Não-admin: apenas marcas ativas.
   */
  const filteredBrands = useMemo(() => {
    if (isUserAdmin) {
      return brands;
    }
    return brands.filter(brand => brand.enable);
  }, [brands, isUserAdmin]);

  const handleBrandClick = (brand) => {
    // Seleciona a marca no estado - produtos serão filtrados localmente
    dispatch(selectBrand(brand));
    console.log('Marca selecionada (dados locais):', brand.name);
  };

  const handleEditBrand = (brand) => {
    setSelectedBrandEdit(brand);
    setEditModalOpen(true);
  };

  const handleSaveBrand = async (data) => {
    try {
      // Cenário B: Atualização remota (admin)
      await dispatch(updateBrand(data)).unwrap();
      
      // Atualiza contexto global após operação bem-sucedida
      await globalContextManager.forceUpdate('brands');
      
      setSnackbar({ open: true, message: 'Marca atualizada com sucesso', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao atualizar marca', severity: 'error' });
      throw error;
    }
  };

  // Loading e erro são gerenciados pelo AppInitializer via contexto global

  return (
    <Box component='main' sx={{ pt: 8, pb: 6, minHeight: '100vh' }}>
      <Container
        maxWidth='lg'
        sx={{
          py: 4,
          mt: { xs: 5, sm: 5, md: '100px' },
          px: { xs: 1, sm: 3, md: 3 },
        }}
      >
        <Typography
          variant='h5'
          component='h5'
          sx={{
            mt: { xs: 8, sm: 8, md: 8 },
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          Marcas de Produtos
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid
            container
            spacing={{ xs: 1, sm: 3, md: 3 }}
            sx={{
              maxWidth: { xs: '100%', sm: 1000, md: 1200 },
              width: '100%',
              margin: 0,
              mt: { xs: 1, md: 1 },
            }}
          >
            {filteredBrands.length > 0 ? (
              filteredBrands.map(brand => (
                <Grid
                  key={brand.id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: 0,
                    maxWidth: '100%',
                  }}
                >
                  <div
                    ref={el => brandRefs.current[brand.id] = el}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <BrandCard
                      id={brand.id}
                      name={brand.name}
                      enable={brand.enable}
                      image_url={brand.image_url}
                      isAdmin={isUserAdmin}
                      onClick={handleBrandClick}
                      onEdit={isUserAdmin ? handleEditBrand : undefined}
                    />
                  </div>
                </Grid>
              ))
            ) : (
              <Grid size={12}>
                <Alert severity='info'>Nenhuma marca encontrada</Alert>
              </Grid>
            )}
          </Grid>
        </Box>

        <BrandEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          brand={selectedBrandEdit}
          onSave={handleSaveBrand}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Container>
    </Box>
  );
}
